import React, { useMemo, useState, useCallback } from 'react';
import {
    Button,
    ButtonGroup,
    IconButton,
    makeStyles,
    Typography,
} from '@material-ui/core';
import ExportButton from 'components/ExportButton';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import Layout from '../layout';
import UsageDetailTable from './components/UsageDetailTable';
import { exportUsageAnalytics } from 'modules/api/usageAnalytics';
import { ROUTE_HISTORICAL, ROUTE_USAGE_ANALYTICS } from 'app/routes';

const useStyles = makeStyles((theme) => ({
    pageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(2.5),
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    backBtn: {
        padding: 4,
        color: '#111',
    },
    title: {
        fontSize: 28,
        fontWeight: 700,
        color: '#111',
        lineHeight: 1.2,
    },
    subtitle: {
        fontSize: 13,
        fontWeight: 500,
        color: '#0B3F30',
        marginTop: 2,
    },
    headerRight: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
    },
    periodBtn: {
        textTransform: 'uppercase',
        fontSize: 11,
        fontWeight: 400,
        letterSpacing: '0.07em',
        padding: '6px 14px',
    },
    btnActive: {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        '&:hover': { backgroundColor: theme.palette.primary.dark },
    },
    btnInactive: {
        color: '#000',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
    },
}));

export default function UsageDetail() {
    const classes = useStyles();
    const history = useHistory();

    const thisMonth = useMemo(() => dayjs().startOf('month').format('YYYY-MM-DD'), []);
    const prevMonth = useMemo(() => dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), []);
    const prev2Month = useMemo(() => dayjs().subtract(2, 'month').startOf('month').format('YYYY-MM-DD'), []);

    const PERIODS = useMemo(
        () => [
            { key: prev2Month, label: 'UŽPRAĖJĘS MĖNUO' },
            { key: prevMonth, label: 'PRAĖJĘS MĖNUO' },
            { key: thisMonth, label: 'ŠIS MĖNUO' },
        ],
        [prev2Month, prevMonth, thisMonth],
    );

    const PERIOD_LABELS = useMemo(
        () => ({
            [thisMonth]: 'Šis mėnuo',
            [prevMonth]: 'Praėjęs mėnuo',
            [prev2Month]: 'Užpraėjęs mėnuo',
        }),
        [thisMonth, prevMonth, prev2Month],
    );

    const [selectedMonth, setSelectedMonth] = useState(thisMonth);
    const [columnFilters, setColumnFilters] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(null);

    const handleExport = useCallback(async () => {
        setExporting(true);
        setExportError(null);
        try {
            const filters = {};
            for (const f of columnFilters) {
                if (!f.value) continue;
                const v = f.value;
                if (v.gte !== undefined) filters[`${f.id}Gte`] = v.gte;
                if (v.lte !== undefined) filters[`${f.id}Lte`] = v.lte;
                if (v.value !== undefined) filters[f.id] = v.value;
            }
            await exportUsageAnalytics({
                monthStart: selectedMonth,
                filters,
            });
        } catch (e) {
            setExportError(e.message || 'Eksporto klaida');
        } finally {
            setExporting(false);
        }
    }, [columnFilters, selectedMonth]);

    const subtitleMonth = dayjs(selectedMonth).format('MMMM YYYY');
    const subtitleLabel = PERIOD_LABELS[selectedMonth] || dayjs(selectedMonth).format('MMMM YYYY');

    return (
        <Layout>
            {/* Page header */}
            <div className={classes.pageHeader}>
                <div>
                    <div className={classes.titleRow}>
                        <IconButton
                            className={classes.backBtn}
                            onClick={() => history.push(ROUTE_USAGE_ANALYTICS.path)}
                            aria-label="Atgal"
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography className={classes.title}>Detalizacija</Typography>
                    </div>
                    <Typography className={classes.subtitle} style={{ textTransform: 'capitalize' }}>
                        Rodoma: {subtitleMonth} ({subtitleLabel})
                    </Typography>
                </div>

                <div className={classes.headerRight}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ButtonGroup disableElevation>
                            {PERIODS.map((period) => (
                                <Button
                                    key={period.key}
                                    className={`${classes.periodBtn} ${
                                        selectedMonth === period.key
                                            ? classes.btnActive
                                            : classes.btnInactive
                                    }`}
                                    onClick={() => setSelectedMonth(period.key)}
                                >
                                    {period.label}
                                </Button>
                            ))}
                        </ButtonGroup>
                        <Button
                            className={`${classes.periodBtn} ${classes.btnInactive}`}
                            endIcon={<CalendarTodayIcon style={{ fontSize: 13 }} />}
                            onClick={() => history.push(ROUTE_HISTORICAL.path)}
                        >
                            Istoriniai duomenys
                        </Button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                        <ExportButton
                            loading={exporting}
                            onClick={handleExport}
                        />
                        {exportError && (
                            <Typography style={{ color: '#c62828', fontSize: 12, alignSelf: 'center' }}>
                                {exportError}
                            </Typography>
                        )}
                    </div>
                </div>
            </div>

            {/* Data table */}
            <UsageDetailTable
                monthStart={selectedMonth}
                compareMonth={selectedMonth === prevMonth ? prev2Month : null}
                onFiltersChange={setColumnFilters}
            />
        </Layout>
    );
}
